import { useContext } from 'react'
import { useParams } from 'react-router-dom'

import { useArticleDetailApi, useUpdateArticleMutation } from '@/common/api/modules/articles'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  articlePartTypeLabel,
  articleProductListSummary,
  articleStatusBadgeType,
  articleStatusChoices,
  articleStatusLabel,
  articleToneLabel
} from '@/common/app/modules/media/article'
import { articleEditPath, articleListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'
import {
  ArticlePartRecord,
  ArticleProposalDetail,
  ArticleStatus
} from '@/common/model/media/article'
import { Site } from '@/common/model/site'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import BackButton from '@/components/view/atoms/button/BackButton'
import Button from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import Side from '@/components/view/layouts/page/Side'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import DisclosureSelector from '@/components/view/molecules/selector/DisclosureSelector'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import ArticleDiagnosisModalProvider, {
  ArticleDiagnosisModalContext
} from '@/components/container/modal/article/ArticleDiagnosisModalProvider'
import ArticleDiffModalProvider from '@/components/container/modal/article/ArticleDiffModalProvider'
import ActionDetailModalProvider from '@/components/container/modal/automationReview/ActionDetailModalProvider'
import ArticleUpdateHistory from '@/components/container/modules/media/article/ArticleUpdateHistory'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

const ContentPartBlock = ({ part }: { part: ArticlePartRecord }) => (
  <>
    <Text size={12} weight='bold'>
      {articlePartTypeLabel(part.part_type)}
    </Text>
    <InsideArea color={BgColor.SECONDARY}>
      {part.image_path && <img src={part.image_path} alt='' />}
      {part.part_type === 'page_title' ? (
        <Text size={12} color={TextColor.SECONDARY}>
          {part.page_title || '未設定'}
        </Text>
      ) : part.content_html ? (
        part.part_type === 'product_list' ? (
          <Text size={12} color={TextColor.SECONDARY}>
            {articleProductListSummary(part.content_html)}
          </Text>
        ) : (
          <div
            className='text-size-12 text-primary'
            // article_parts.content_html はアプリ内で生成/編集された記事コンテンツのため dangerouslySetInnerHTML を使用
            dangerouslySetInnerHTML={{ __html: part.content_html }}
          />
        )
      ) : (
        !part.image_path && (
          <Text size={12} color={TextColor.SECONDARY}>
            未設定
          </Text>
        )
      )}
    </InsideArea>
  </>
)

const ArticleDetailViewInner = ({
  site,
  articleProposalId,
  detail,
  onRefetch
}: {
  site: Site
  articleProposalId: number
  detail: ArticleProposalDetail
  onRefetch(): void
}) => {
  const { showModal: showArticleDiagnosis } = useContext(ArticleDiagnosisModalContext)
  const { addMessage } = useContext(ToastMessageContext)
  const { feature_page: featurePage, latest_article: latest, parts, history } = detail
  const { trigger: updateArticle } = useUpdateArticleMutation({
    onSuccess: () => {
      onRefetch()
      addMessage({ text: 'ステータスを更新しました', type: MessageType.SUCCESS })
    }
  })

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <BackButton link={articleListPath(site.id)} />
      <PageTitle
        title={latest.title || '（無題の記事）'}
        behindNode={
          <Badge badgeType={articleStatusBadgeType(latest.status)} fixed>
            {articleStatusLabel(latest.status)}
          </Badge>
        }
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        rightNode={
          <>
            <AiButton
              size={'large'}
              link={`${articleEditPath(site.id, articleProposalId, { openAiRewrite: true })}`}
            >
              AIリライトする
            </AiButton>
            <AiButton
              size={'large'}
              onClick={() => showArticleDiagnosis({ siteId: site.id, articleProposalId })}
            >
              AI診断する
            </AiButton>
            <Button link={articleEditPath(site.id, articleProposalId)} size={'large'}>
              編集する
            </Button>
          </>
        }
      />

      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <Area bottomMargin={16}>
            <Heading>特集記事概要</Heading>
            <AreaContent>
              <FormRow align='center'>
                <FormLabel>記事ID</FormLabel>
                <Text size={12}>{featurePage.feature_page_id ?? '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>カテゴリ</FormLabel>
                <Text size={12}>{featurePage.category_id || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>管理ページ名</FormLabel>
                <Text size={12}>{featurePage.management_title || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>記事URL（スラッグ）</FormLabel>
                <Text size={12}>{latest.slug || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>HTMLページタイトル</FormLabel>
                <Text size={12}>{latest.title || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>SEOディスクリプション</FormLabel>
                <Text size={12}>{latest.meta_description || '-'}</Text>
              </FormRow>
            </AreaContent>
          </Area>
          <Area>
            <Heading>記事情報</Heading>
            <AreaContent>
              <FormRow align='center'>
                <FormLabel>アクセス(30日)</FormLabel>
                {/* <Text size={12}> */}
                {/* {metrics.views_30d.toLocaleString()} 件 ・ 平均滞在{' '}
                  {metrics.avg_dwell_sec.toFixed(1)}秒 */}
                {/* </Text> */}
              </FormRow>
              <FormRow align='center'>
                <FormLabel>キーワード</FormLabel>
                <ItemAlign gapX={4} justify='center'>
                  {latest.keyword?.split(/[ ,、]/).map((k) => (
                    <Badge key={k} size={10} badgeType={BadgeType.KEYWORD}>
                      {k}
                    </Badge>
                  )) || <Text size={12}>-</Text>}
                </ItemAlign>
              </FormRow>
              <FormRow align='center'>
                <FormLabel subText='1行1見出し、空欄の場合はAIが生成'>見出し構成案</FormLabel>
                <Text size={12}>{latest.heading_outline || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>トーン</FormLabel>
                <Text size={12}>{articleToneLabel(latest.tone) || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel>目標文字数</FormLabel>
                <Text size={12}>
                  {latest.target_char_count
                    ? latest.target_char_count.toLocaleString() + '文字'
                    : '-'}
                </Text>
              </FormRow>
            </AreaContent>
          </Area>
          <Area>
            <Heading>コンテンツブロック</Heading>
            <AreaContent>
              {parts.length === 0 ? (
                <Text size={12} color={TextColor.SECONDARY} align='center'>
                  コンテンツブロックがありません
                </Text>
              ) : (
                parts.map((part) => <ContentPartBlock key={part.id} part={part} />)
              )}
            </AreaContent>
          </Area>
        </Content>

        <Side>
          <Area bottomMargin={16}>
            <Heading>利用状況</Heading>
            <AreaContent>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>ステータス</FormLabel>
                <DisclosureSelector<string>
                  value={latest.status}
                  name={'status'}
                  choices={articleStatusChoices(site.enable_article_upload)}
                  disabled={latest.status === ArticleStatus.PENDING_APPROVAL}
                  onChange={(value) =>
                    updateArticle({
                      site_id: site.id,
                      article_id: latest.id,
                      status: value as ArticleStatus
                    })
                  }
                  remnant
                />
              </FormRow>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>最終更新者</FormLabel>
                <Text size={12}>{latest.site_operator_name || '-'}</Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel fixWidth={80}>最終更新日</FormLabel>
                <Text size={12}>{latest.updated_at ? dateTimeStr(latest.updated_at) : '-'}</Text>
              </FormRow>
            </AreaContent>
          </Area>
          <ArticleUpdateHistory
            siteId={site.id}
            articleProposalId={articleProposalId}
            history={history}
          />
        </Side>
      </ContentSideFrame>
    </Main>
  )
}

// 記事詳細（記事案の最新バージョンを表示）
const ArticleDetailPage = () => {
  const siteId = useSiteId()
  const { articleId } = useParams()
  const articleProposalId = Number(articleId)

  const { data, error, mutate } = useArticleDetailApi(
    articleId ? { site_id: siteId, article_proposal_id: articleProposalId } : null
  )
  const { data: site, error: siteError } = useSiteGetApi({ site_id: siteId })

  return (
    <DetailPageLoader error={error || siteError} data={data && site}>
      {data && site && (
        <ArticleDiagnosisModalProvider>
          <ArticleDiffModalProvider>
            <ActionDetailModalProvider>
              <ArticleDetailViewInner
                site={site}
                articleProposalId={articleProposalId}
                detail={data}
                onRefetch={() => mutate()}
              />
            </ActionDetailModalProvider>
          </ArticleDiffModalProvider>
        </ArticleDiagnosisModalProvider>
      )}
    </DetailPageLoader>
  )
}

export default ArticleDetailPage
